import { useState } from 'react';
import {
  Box,
  Container,
  Stack,
  Typography,
  Paper,
  Button,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Chip,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Divider,
  Link,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import QuizIcon from '@mui/icons-material/Quiz';
import BarChartIcon from '@mui/icons-material/BarChart';
import SendIcon from '@mui/icons-material/Send';

// Knowledge base articles
const knowledgeArticles = [
  {
    id: 1,
    title: 'Getting Started with Agents',
    description: 'Learn how to create, configure, and manage AI agents connected to CXCortex.',
    icon: SmartToyIcon,
    category: 'Basics',
    content: `
## Creating Your First Agent

1. Navigate to the **Agents** page from the sidebar
2. Click **Add Agent** and enter a name
3. Paste your CXCortex endpoint URL (e.g., \`https://agents.cxcortex.ai/api/v1/prediction/xxxxx\`)
4. Optionally add an API key if your CXCortex endpoint requires authentication
5. Click **Save**

## Managing Agents

- **Edit**: Update the agent name, endpoint, or API key
- **Delete**: Remove the agent and all associated tasks/chats
- **Integrations**: Connect Microsoft Teams for quiz distribution

## Best Practices

- Use descriptive names that reflect the agent's purpose
- Test your CXCortex endpoint before adding it
- Keep API keys secure and rotate them periodically
    `,
  },
  {
    id: 2,
    title: 'Scheduling Tasks',
    description: 'How to schedule automated prompts to run at specific times.',
    icon: CalendarMonthIcon,
    category: 'Basics',
    content: `
## Task Scheduling Options

- **Now**: Execute immediately (one-time)
- **Every Minute**: Run every minute for 5 minutes (testing)
- **Hourly**: Run every hour for 5 hours
- **Daily**: Run daily at a specific time for 3 months
- **Weekly**: Run weekly on a specific day/time for 3 months
- **Monthly**: Run monthly on a specific day/time for 3 months
- **Custom Time**: One-time execution at a specific date/time

## Creating a Scheduled Task

1. Go to the **Instructions** page
2. Select an agent from the dropdown
3. Enter your instruction/prompt
4. Choose a schedule type
5. Configure the schedule details (time, day, etc.)
6. Click **Schedule**

## Viewing Task Results

- Check the **Calendar** page for upcoming/past executions
- Click on a task to see the AI response and execution time
- View analytics on the **Analytics** page
    `,
  },
  {
    id: 3,
    title: 'Microsoft Teams Integration',
    description: 'Connect your agents to Teams for quiz distribution and announcements.',
    icon: IntegrationInstructionsIcon,
    category: 'Integrations',
    content: `
## Setting Up Teams Integration

1. Go to **Agents** page and select an agent
2. Click **Connect Teams** under Integrations
3. Sign in with your Microsoft account
4. Grant the required permissions
5. Wait for the connection to complete

## What You Can Do

- **Send Quizzes**: Distribute training quizzes to team members via 1:1 chat
- **Broadcast Announcements**: Post messages to channels
- **Track Responses**: Monitor quiz submissions and grades
- **View Reports**: See participation and performance metrics

## Syncing Team Members

1. After connecting Teams, click **Sync Teams**
2. Enable/disable specific teams for quiz distribution
3. Sync members for each team
4. Toggle individual members on/off

## Troubleshooting

- If connection fails, try disconnecting and reconnecting
- Ensure your Microsoft account has Teams access
- Check that the Azure AD app has required permissions
    `,
  },
  {
    id: 4,
    title: 'Quiz System & Gamification',
    description: 'Understanding the quiz system, XP, levels, and achievements.',
    icon: QuizIcon,
    category: 'Features',
    content: `
## How Quizzes Work

1. The AI agent sends a quiz via Teams chat
2. The team member responds with their answers
3. The agent grades the response and awards XP
4. Results appear on the leaderboard

## XP & Leveling System

| Level | Title | XP Required |
|-------|-------|-------------|
| 1 | Newcomer | 0 |
| 2 | Learner | 100 |
| 3 | Student | 300 |
| 4 | Apprentice | 600 |
| 5 | Scholar | 1,000 |
| 6 | Adept | 1,750 |
| 7 | Expert | 2,750 |
| 8 | Master | 4,000 |
| 9 | Grandmaster | 6,000 |
| 10 | Legend | 10,000 |

## Achievements

Earn badges for milestones like:
- First quiz completed
- Perfect scores
- Streak milestones (3, 7, 14, 30 days)
- XP thresholds

## Viewing Reports

Go to **Quiz Reports** to see:
- Leaderboards (individual and team)
- Participation rates
- Recent activity
- Performance trends
    `,
  },
  {
    id: 5,
    title: 'API Keys & External Access',
    description: 'Generate API keys for PowerBI, external dashboards, and integrations.',
    icon: BarChartIcon,
    category: 'Advanced',
    content: `
## Creating an API Key

1. Go to **API Keys** page
2. Select an agent
3. Click **Generate New Key**
4. Set permissions (Reports, Quiz, Teams)
5. Copy the key immediately (it won't be shown again!)

## Using API Keys

Include the key in request headers:
\`\`\`
x-quiz-api-key: your_api_key_here
\`\`\`

## Available Endpoints

- \`GET /api/agents/:id/reports/dashboard\` - Summary metrics
- \`GET /api/agents/:id/reports/participation\` - Who completed quizzes
- \`GET /api/agents/:id/reports/trends\` - Performance over time
- \`GET /api/quiz/leaderboard/:id\` - Current rankings
- \`GET /api/agents/:id/reports/export?format=csv\` - Export data

## PowerBI Integration

1. Use **Web** as your data source
2. Enter the endpoint URL
3. Set authentication to **Custom Header**
4. Add \`x-quiz-api-key\` with your API key
5. Transform data as needed
    `,
  },
];

// FAQ items
const faqItems = [
  {
    question: 'Why isn\'t my scheduled task running?',
    answer: 'Check that: (1) The task status is "pending" not "completed", (2) Your CXCortex endpoint is accessible, (3) The scheduled time is correct. Tasks run within a 5-minute window of the scheduled time. Daily/weekly/monthly tasks expire after 3 months.',
  },
  {
    question: 'How do I re-send the same quiz to a different person?',
    answer: 'Use the lookup_quiz tool to find a previously sent quiz by title or recipient name, then use send_quiz with the returned questions array. The AI agent can do this automatically if you ask it to "send the same quiz as [person] to [new person]".',
  },
  {
    question: 'Why can\'t the Teams integration see my team members?',
    answer: 'After connecting Teams, you need to: (1) Click "Sync Teams" to pull in your teams, (2) Enable the teams you want to use, (3) Click "Sync Members" for each team. Only enabled members will receive quizzes.',
  },
  {
    question: 'What happens to my data if I delete an agent?',
    answer: 'Deleting an agent removes all associated data including tasks, task results, chat history, integrations, and quiz sessions. This action cannot be undone.',
  },
  {
    question: 'How is the API key stored securely?',
    answer: 'CXCortex API keys are encrypted using AES-256-CBC before storage. The encryption key is stored as an environment variable, not in the database. Keys are decrypted only when making requests to CXCortex.',
  },
  {
    question: 'Can I use multiple agents with the same CXCortex endpoint?',
    answer: 'Yes, you can create multiple agents pointing to the same CXCortex chatflow. This is useful for separating different use cases (e.g., one for scheduling, one for Teams quizzes) while using the same underlying AI.',
  },
  {
    question: 'How do I export quiz data for reporting?',
    answer: 'Go to API Keys, generate a key with Reports permission, then use the /reports/export endpoint with ?format=csv or ?format=json. You can also connect PowerBI directly using the connection info provided.',
  },
  {
    question: 'What\'s the difference between Analytics and Quiz Reports?',
    answer: 'Analytics shows task execution metrics (response times, success rates, agent performance). Quiz Reports shows gamification data (leaderboards, XP, participation, quiz scores).',
  },
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export const SupportPage = () => {
  const [tabValue, setTabValue] = useState(0);
  const [selectedArticle, setSelectedArticle] = useState<typeof knowledgeArticles[0] | null>(null);
  const [expandedFaq, setExpandedFaq] = useState<string | false>(false);
  const [ticketForm, setTicketForm] = useState({
    subject: '',
    description: '',
    email: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setSelectedArticle(null);
  };

  const handleFaqChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedFaq(isExpanded ? panel : false);
  };

  const handleTicketSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real implementation, this would submit to your support system
    console.log('Support ticket:', ticketForm);
    setSnackbar({ open: true, message: 'Support ticket submitted successfully! We\'ll respond within 24 hours.', severity: 'success' });
    setTicketForm({ subject: '', description: '', email: '' });
  };

  const categories = [...new Set(knowledgeArticles.map(a => a.category))];

  return (
    <Box sx={{ py: 4, minHeight: '100%', bgcolor: '#F9FAFB' }}>
      <Container maxWidth="lg">
        <Stack spacing={4}>
          {/* Header */}
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 1 }}>
              Help & Support
            </Typography>
            <Typography variant="body1" sx={{ color: '#6B7280' }}>
              Find answers, learn features, or get in touch with support
            </Typography>
          </Box>

          {/* Tabs */}
          <Paper sx={{ borderRadius: 2 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              sx={{
                borderBottom: '1px solid #E5E7EB',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.95rem',
                },
              }}
            >
              <Tab icon={<MenuBookIcon />} iconPosition="start" label="Knowledge Base" />
              <Tab icon={<QuestionAnswerIcon />} iconPosition="start" label="FAQ" />
              <Tab icon={<SupportAgentIcon />} iconPosition="start" label="Contact Support" />
            </Tabs>

            {/* Knowledge Base Tab */}
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ p: 3 }}>
                {selectedArticle ? (
                  // Article detail view
                  <Box>
                    <Button
                      onClick={() => setSelectedArticle(null)}
                      sx={{ mb: 2, textTransform: 'none' }}
                    >
                      ← Back to articles
                    </Button>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                      {selectedArticle.title}
                    </Typography>
                    <Chip
                      label={selectedArticle.category}
                      size="small"
                      sx={{ mb: 3, bgcolor: '#E0F2FE', color: '#0369A1' }}
                    />
                    <Paper
                      sx={{
                        p: 3,
                        bgcolor: '#FAFAFA',
                        '& h2': { fontSize: '1.25rem', fontWeight: 600, mt: 3, mb: 1.5, color: '#1a1a2e' },
                        '& p': { mb: 2, lineHeight: 1.7, color: '#374151' },
                        '& ul, & ol': { pl: 3, mb: 2 },
                        '& li': { mb: 0.75, color: '#374151' },
                        '& code': {
                          bgcolor: '#F3F4F6',
                          px: 0.75,
                          py: 0.25,
                          borderRadius: 1,
                          fontSize: '0.875rem',
                          fontFamily: 'monospace',
                        },
                        '& pre': {
                          bgcolor: '#1F2937',
                          color: '#F9FAFB',
                          p: 2,
                          borderRadius: 1,
                          overflow: 'auto',
                          '& code': { bgcolor: 'transparent', color: 'inherit' },
                        },
                        '& table': {
                          width: '100%',
                          borderCollapse: 'collapse',
                          mb: 2,
                          '& th, & td': {
                            border: '1px solid #E5E7EB',
                            p: 1,
                            textAlign: 'left',
                          },
                          '& th': { bgcolor: '#F3F4F6', fontWeight: 600 },
                        },
                      }}
                    >
                      <div
                        dangerouslySetInnerHTML={{
                          __html: selectedArticle.content
                            .replace(/^## (.+)$/gm, '<h2>$1</h2>')
                            .replace(/^### (.+)$/gm, '<h3>$1</h3>')
                            .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                            .replace(/`([^`]+)`/g, '<code>$1</code>')
                            .replace(/^- (.+)$/gm, '<li>$1</li>')
                            .replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>')
                            .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
                            .replace(/\n\n/g, '</p><p>')
                            .replace(/^\|(.+)\|$/gm, (match) => {
                              const cells = match.split('|').filter(c => c.trim());
                              if (cells.every(c => c.trim() === '---' || c.trim().match(/^-+$/))) return '';
                              const isHeader = !match.includes('|---|');
                              const tag = isHeader ? 'td' : 'td';
                              return `<tr>${cells.map(c => `<${tag}>${c.trim()}</${tag}>`).join('')}</tr>`;
                            })
                            .replace(/(<tr>.*<\/tr>\n?)+/g, '<table>$&</table>')
                        }}
                      />
                    </Paper>
                  </Box>
                ) : (
                  // Article list view
                  <Box>
                    {categories.map(category => (
                      <Box key={category} sx={{ mb: 4 }}>
                        <Typography
                          variant="overline"
                          sx={{
                            color: '#6B7280',
                            fontWeight: 600,
                            letterSpacing: '0.1em',
                            display: 'block',
                            mb: 2,
                          }}
                        >
                          {category}
                        </Typography>
                        <Grid container spacing={2}>
                          {knowledgeArticles
                            .filter(a => a.category === category)
                            .map(article => {
                              const Icon = article.icon;
                              return (
                                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={article.id}>
                                  <Card
                                    sx={{
                                      height: '100%',
                                      transition: 'all 0.2s',
                                      '&:hover': {
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                        transform: 'translateY(-2px)',
                                      },
                                    }}
                                  >
                                    <CardActionArea
                                      onClick={() => setSelectedArticle(article)}
                                      sx={{ height: '100%', p: 2 }}
                                    >
                                      <CardContent sx={{ p: 0 }}>
                                        <Box
                                          sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: 2,
                                            bgcolor: '#E0F2FE',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: 2,
                                          }}
                                        >
                                          <Icon sx={{ color: '#0099CC' }} />
                                        </Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                          {article.title}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#6B7280' }}>
                                          {article.description}
                                        </Typography>
                                      </CardContent>
                                    </CardActionArea>
                                  </Card>
                                </Grid>
                              );
                            })}
                        </Grid>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </TabPanel>

            {/* FAQ Tab */}
            <TabPanel value={tabValue} index={1}>
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Frequently Asked Questions
                </Typography>
                {faqItems.map((faq, index) => (
                  <Accordion
                    key={index}
                    expanded={expandedFaq === `faq-${index}`}
                    onChange={handleFaqChange(`faq-${index}`)}
                    sx={{
                      mb: 1,
                      '&:before': { display: 'none' },
                      boxShadow: 'none',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px !important',
                      '&.Mui-expanded': {
                        margin: '0 0 8px 0',
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        '& .MuiAccordionSummary-content': {
                          my: 1.5,
                        },
                      }}
                    >
                      <Typography sx={{ fontWeight: 500 }}>{faq.question}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography sx={{ color: '#4B5563', lineHeight: 1.7 }}>
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}

                <Divider sx={{ my: 4 }} />

                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body1" sx={{ mb: 2, color: '#6B7280' }}>
                    Can't find what you're looking for?
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => setTabValue(2)}
                    startIcon={<SupportAgentIcon />}
                    sx={{ textTransform: 'none' }}
                  >
                    Contact Support
                  </Button>
                </Box>
              </Box>
            </TabPanel>

            {/* Contact Support Tab */}
            <TabPanel value={tabValue} index={2}>
              <Box sx={{ p: 3 }}>
                <Grid container spacing={4}>
                  {/* Contact Form */}
                  <Grid size={{ xs: 12, md: 7 }}>
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                      Submit a Support Ticket
                    </Typography>
                    <form onSubmit={handleTicketSubmit}>
                      <Stack spacing={3}>
                        <TextField
                          label="Your Email"
                          type="email"
                          required
                          fullWidth
                          value={ticketForm.email}
                          onChange={(e) => setTicketForm({ ...ticketForm, email: e.target.value })}
                          placeholder="you@company.com"
                        />
                        <TextField
                          label="Subject"
                          required
                          fullWidth
                          value={ticketForm.subject}
                          onChange={(e) => setTicketForm({ ...ticketForm, subject: e.target.value })}
                          placeholder="Brief description of your issue"
                        />
                        <TextField
                          label="Description"
                          required
                          fullWidth
                          multiline
                          rows={5}
                          value={ticketForm.description}
                          onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })}
                          placeholder="Please describe your issue in detail. Include any error messages, steps to reproduce, and what you expected to happen."
                        />
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          startIcon={<SendIcon />}
                          sx={{
                            textTransform: 'none',
                            bgcolor: '#0099CC',
                            '&:hover': { bgcolor: '#007AA3' },
                          }}
                        >
                          Submit Ticket
                        </Button>
                      </Stack>
                    </form>
                  </Grid>

                  {/* Contact Info */}
                  <Grid size={{ xs: 12, md: 5 }}>
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                      Other Ways to Reach Us
                    </Typography>
                    <Stack spacing={2}>
                      <Paper sx={{ p: 2.5, bgcolor: '#F9FAFB' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          📧 Email
                        </Typography>
                        <Link href="mailto:helpdesk@hostcomm.co.uk" sx={{ color: '#0099CC' }}>
                          helpdesk@hostcomm.co.uk
                        </Link>
                      </Paper>
                      <Paper sx={{ p: 2.5, bgcolor: '#F9FAFB' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          ⏰ Response Time
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6B7280' }}>
                          We typically respond within 1 hour during business days.
                        </Typography>
                      </Paper>
                    </Stack>

                    <Alert severity="info" sx={{ mt: 3 }}>
                      For urgent issues affecting production, please include "URGENT" in your subject line.
                    </Alert>
                  </Grid>
                </Grid>
              </Box>
            </TabPanel>
          </Paper>
        </Stack>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default SupportPage;
